import React from 'react';
import {
  Alert, Box, Divider, Link, Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import useRUMView from 'common-datadog/useRUMView';
import useApplication from '../../resources/applications/read';
import useRepair from '../../resources/repairs/read';
import Screen from '../Screen';
import ButtonPrimary from '../ButtonPrimary';
import InstallmentWaitingForServiceAdvisor from '../InstallmentWaitingForServiceAdvisor';
import { standardizePhone } from '../../utils/phone';
import { currencyFormat } from '../../constants';
import useForm from './useForm';
import Form from '../Form';
import InputFinancingAmount from '../InputFinancingAmount';

const InstallmentFinancingAmount = () => {
  useRUMView('installment-financing-amount');
  const { data: application } = useApplication();
  const { data: repair } = useRepair();
  const merchantPhone = standardizePhone(application.attributes.service_contact_info[1]);

  const form = useForm();
  const {
    handleSubmit,
    isLoading,
    watch,
  } = form;

  const financingAmount = watch('financingAmount');

  if (application.attributes.waiting_for_sa) {
    return <InstallmentWaitingForServiceAdvisor />;
  }

  return (
    <Screen
      header={(
        <>
          {application.attributes.service_advisor_name}
          {' '}
          has entered your transaction information.
        </>
        )}
      subheader={(
        <>
          Please review to ensure everything is accurate.
          If you find any issues contact
          {' '}
          {application.attributes.service_advisor_name}
          {merchantPhone
            ? (
              <>
                {' '}
                at
                {' '}
                <Link href={`tel:${merchantPhone.normalized}`}>{merchantPhone.formatted}</Link>
                .
              </>
            )
            : '.'}
        </>
          )}
    >
      <Typography variant="body2">
        Total Transaction Amount:
      </Typography>
      <Typography variant="h1" sx={{ mt: -2 }} color="secondary">
        {currencyFormat.format(repair.attributes.total_amount)}
      </Typography>
      <Divider />
      <Typography variant="h3">
        Financing Amount
      </Typography>
      <Typography variant="body1" sx={{ mt: -2 }}>
        Enter the amount you would like to finance and click Next Step.
        Once loan documents have been signed, this amount cannot be changed.
      </Typography>
      <Form form={form} onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">Max Approved Amount:</Typography>
          <Typography variant="subtitle2">
            {currencyFormat.format(application.attributes.max_approved_amount)}
          </Typography>
        </Box>
        <InputFinancingAmount />
        { +repair.attributes.total_amount - +financingAmount > 0
          && (
          <Alert icon={<WarningAmberIcon fontSize="inherit" />} severity="warning">
            <Typography variant="body2" align="left">
              You will be responsible to pay
              {' '}
              {application.attributes.dealership_name}
              {' '}
              the remaining balance of
              {' '}
              <b>
                {currencyFormat.format(+repair.attributes.total_amount - +financingAmount)}
              </b>
              {' '}
              when picking up your vehicle.
            </Typography>
          </Alert>
          )}
        <Typography variant="body2">
          Please note, if you use less than your maximum approved amount, you
          will not be able to use the remaining amount for another transaction.
          Any additional repair work that you would like to finance will require
          a new loan application. Other restrictions may apply.
        </Typography>
        <ButtonPrimary isLoading={isLoading}>
          Next Step
        </ButtonPrimary>
      </Form>
    </Screen>
  );
};

export default InstallmentFinancingAmount;
